import _isEmpty from 'lodash/isEmpty';
import { createAction, createReducer } from 'redux-act';

import { GetBannersParams } from '../__generated__/gateway/Api';
import { TAdMaterialTagsList, TBannersList } from '../interfaces/advertising';
import { IMaterial } from '../interfaces/news';
import { getAdMaterialTags as getAdMaterialTagsSelector } from '../selectors/advertising';
import { getMaterialsList } from '../selectors/news';
import { getAdMaterialTags, getBanners, IBannersParams } from '../services/advertising';
import { App } from '../types';

type LoadingMap = Record<GetBannersParams['BannerTypes'][number], boolean>;

export interface IState {
  banners: { [articleAlias: string]: TBannersList } & {
    common?: TBannersList;
  };
  loading: LoadingMap;
  loaded: boolean;
  adMaterialTags: TAdMaterialTagsList;
}

const initialState: IState = {
  banners: null,
  adMaterialTags: {},
  loading: null,
  loaded: false,
};

export const setBanners = createAction<{ banners: TBannersList; material?: IMaterial }>('advertising/banners/set');
export const startLoadingBanners = createAction<GetBannersParams['BannerTypes']>('advertising/banners/startLoading');
export const setAdMaterialTags = createAction<IState['adMaterialTags']>('advertising/adMaterialTags/set');

export const fetchBanners = (params: IBannersParams): App.ReduxAction => {
  return async (dispatch) => {
    try {
      dispatch(startLoadingBanners(params.params.BannerTypes));
      const data = await getBanners(params);

      dispatch(setBanners({ banners: data, material: params.source }));
    } catch (error) {
      dispatch(setBanners(null));
    }
  };
};

export const fetchBannersWithStoreData = (params: IBannersParams): App.ReduxAction => {
  return (dispatch, getState) => {
    const state = getState();
    dispatch(
      fetchBanners({
        ...params,
        source: getMaterialsList(state)?.[0],
        adTags: getAdMaterialTagsSelector(state),
      }),
    );
  };
};

export const fetchAdMaterialTags = async (): Promise<TAdMaterialTagsList> => {
  const data = await getAdMaterialTags();
  const structuredTags = data.reduce((acc, cur) => {
    return { ...acc, [cur.seoAlias]: cur };
  }, {});

  return structuredTags;
};

export const fetchAdMaterialTagsAction = (): App.ReduxAction => {
  return async (dispatch, getState) => {
    if (!_isEmpty(getAdMaterialTagsSelector(getState()))) return;

    try {
      const data = await fetchAdMaterialTags();

      dispatch(setAdMaterialTags(data));
    } catch (error) {
      dispatch(setAdMaterialTags({}));
    }
  };
};

const advertising = createReducer<IState>({}, initialState)
  .on(startLoadingBanners, (state, payload) => {
    const loadingMap = payload.reduce((acc, type) => ({ ...acc, [type]: true }), {} as LoadingMap);

    return {
      ...state,
      loading: loadingMap,
    };
  })
  .on(setBanners, (state, { banners, material }) => {
    const bannersState = material ? { [material.alias]: banners } : { common: banners };

    return {
      ...state,
      banners: {
        ...state.banners,
        ...bannersState,
      },
      loading: null,
      loaded: true,
    };
  })
  .on(setAdMaterialTags, (state, payload) => ({
    ...state,
    adMaterialTags: payload,
  }));

export default advertising;
